import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import InvoiceForm from '../common/InvoiceForm';
import PrintPreview from '../common/PrintPreview';
import { Plus, Edit2, Trash2, Printer } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/helpers';

export default function SalesReturn() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [printData, setPrintData] = useState(null);

  const handleSave = (data) => {
    if (editData) {
      dispatch({ type: 'DELETE_SALES_RETURN', payload: editData.id });
      dispatch({ type: 'ADD_SALES_RETURN', payload: data });
      addToast('Sales return updated successfully', 'success');
    } else {
      dispatch({ type: 'ADD_SALES_RETURN', payload: data });
      addToast('Sales return created successfully', 'success');
    }
    closeForm();
  };

  const handleEdit = (row) => { setEditData(row); setShowForm(true); };
  const handleDelete = (row) => {
    if (window.confirm(`Delete return ${row.returnNo}? Stock and ledger will be reversed.`)) {
      dispatch({ type: 'DELETE_SALES_RETURN', payload: row.id });
    }
  };
  const closeForm = () => { setShowForm(false); setEditData(null); };

  if (printData) return <PrintPreview type="salesReturn" data={printData} onClose={() => setPrintData(null)} />;

  const columns = [
    { key: 'returnNo', label: 'Return No', sortable: true },
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'partyId', label: 'Customer', render: (v) => state.parties.find(p => p.id === v)?.name || '-' },
    { key: 'againstInvoice', label: 'Against Invoice' },
    { key: 'grandTotal', label: 'Amount', align: 'right', render: (v) => formatCurrency(v) },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Sales Returns</h1>
        <button onClick={() => { setEditData(null); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Return
        </button>
      </div>
      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.salesReturns].reverse()}
          searchFields={['returnNo', 'againstInvoice']}
          emptyState={{ title: 'No returns yet', description: 'Create your first sales return.', actionLabel: 'New Return', onAction: () => setShowForm(true) }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => setPrintData(row)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Print"><Printer size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>
      <Modal isOpen={showForm} onClose={closeForm} title={editData ? `Edit Return ${editData.returnNo}` : 'New Sales Return (Credit Note)'} size="xl">
        <InvoiceForm
          key={editData ? editData.id : 'new'}
          type="salesReturn"
          onSave={handleSave}
          onCancel={closeForm}
          initialData={editData || undefined}
          nextEntryNo={editData ? editData.returnNo : `SR-${String(state.counters.salesReturn + 1).padStart(4, '0')}`}
        />
      </Modal>
    </div>
  );
}
